'use client';

import { c as _c } from "react/compiler-runtime";
import { autoUpdate, useFloating } from '@floating-ui/react';
import { Show, reactive } from '@legendapp/state/react';
import { $React } from '@legendapp/state/react-web';
import { type ChangeEvent, type InputHTMLAttributes, useEffect } from 'react';
import { Typography } from '@/components/Typography';
import { useLocationSelectState } from '@/components/forms/fields/LocationSelect/LocationSelectContext';
import { findSiteInRegion } from '@/services/rental-api-client';
import { ReactiveLabeledValueField } from './LabeledValueField';
import { LocationSelectList } from './LocationSelectList';
export type fieldOptions = {
  name: string;
  label: string;
  placeholder?: string;
  value?: InputHTMLAttributes<HTMLInputElement>['value'];
  onChange?: (e?: ChangeEvent<HTMLInputElement>) => void;
};
export type LocationFieldProps = {
  field: 'pickUp' | 'dropOff';
  fieldOptions: fieldOptions;
  tabIndex?: number;
};
const ReactiveLocationSelectList = reactive(LocationSelectList);
export function LocationField(t0) {
  const $ = _c(68);
  const {
    field,
    fieldOptions,
    tabIndex: t1
  } = t0;
  const tabIndex = t1 === undefined ? 0 : t1;
  const state$ = useLocationSelectState();
  let t2;
  if ($[0] !== state$.regions) {
    t2 = state$.regions.get();
    $[0] = state$.regions;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const regions = t2;
  const location$ = field === "pickUp" ? state$.pickUpLocation : state$.dropOffLocation;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      whileElementsMounted: autoUpdate,
      placement: "bottom-start",
      transform: false
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    refs,
    floatingStyles
  } = useFloating(t3);
  const {
    setReference,
    setFloating,
    reference,
    floating
  } = refs;
  let t4;
  let t5;
  if ($[3] !== field || $[4] !== refs.floating || $[5] !== refs.reference || $[6] !== state$.selecting) {
    t4 = () => {
      const handleClick = function handleClick(event) {
        const trigger = refs.reference.current as HTMLElement;
        const dropDown = refs.floating.current;
        const target = event.target as HTMLElement;
        if (!dropDown || state$.selecting.get() !== field || trigger === target || trigger?.contains(target)) {
          return;
        }
        if (!target || !dropDown.contains(target)) {
          state$.selecting.delete();
        }
      };
      document.addEventListener("mousedown", handleClick);
      return () => {
        document.removeEventListener("mousedown", handleClick);
      };
    };
    t5 = [field, refs.floating, refs.reference, state$.selecting];
    $[3] = field;
    $[4] = refs.floating;
    $[5] = refs.reference;
    $[6] = state$.selecting;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== fieldOptions.label) {
    t6 = <Typography variant="caption" as="div" className="mb-3">{fieldOptions.label}</Typography>;
    $[9] = fieldOptions.label;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = `location-selection-${field}-btn`;
  let t8;
  if ($[11] !== location$) {
    t8 = () => location$.get();
    $[11] = location$;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== field || $[14] !== state$.selecting) {
    t9 = () => state$.selecting.get() === field;
    $[13] = field;
    $[14] = state$.selecting;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== field || $[17] !== floating.current || $[18] !== reference.current || $[19] !== state$.selecting) {
    t10 = e => {
      const trigger_0 = reference.current as HTMLElement;
      const dropDown_0 = floating.current as HTMLElement;
      if (trigger_0 && dropDown_0) {
        dropDown_0.style.setProperty("width", `${trigger_0.clientWidth}px`);
      }
      if (e.target.checked) {
        state$.selecting.set(field);
      } else {
        state$.selecting.delete();
      }
    };
    $[16] = field;
    $[17] = floating.current;
    $[18] = reference.current;
    $[19] = state$.selecting;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = `${field}Branch`;
  const t12 = `${field}-location`;
  let t13;
  if ($[21] !== location$.siteCode) {
    t13 = () => location$.siteCode.get() || "";
    $[21] = location$.siteCode;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t13) {
    t14 = <$React.input type="hidden" name={t11} data-testid={t12} $value={t13} />;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== field || $[28] !== fieldOptions.label || $[29] !== fieldOptions.placeholder || $[30] !== setReference || $[31] !== t10 || $[32] !== t14 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9 || $[36] !== tabIndex) {
    t15 = <ReactiveLabeledValueField ref={setReference} tabIndex={tabIndex} label={fieldOptions.label} name="selectedMode" value={field} data-testid={t7} className="peer" $selectedSite={t8} placeholder={fieldOptions.placeholder} $checked={t9} onChange={t10}>{t14}</ReactiveLabeledValueField>;
    $[27] = field;
    $[28] = fieldOptions.label;
    $[29] = fieldOptions.placeholder;
    $[30] = setReference;
    $[31] = t10;
    $[32] = t14;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = tabIndex;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== state$.variant) {
    t16 = () => state$.variant.get() === "vertical";
    $[38] = state$.variant;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const t17 = `popover-${field}`;
  let t18;
  if ($[40] !== location$) {
    t18 = () => location$.get()?.siteCode;
    $[40] = location$;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== fieldOptions || $[43] !== location$ || $[44] !== regions || $[45] !== state$.selecting) {
    t19 = e_0 => {
      const site = findSiteInRegion(regions, e_0.target.value);
      if (!site) {
        return;
      }
      fieldOptions.onChange?.(e_0);
      location$.set(site);
      state$.selecting.delete();
    };
    $[42] = fieldOptions;
    $[43] = location$;
    $[44] = regions;
    $[45] = state$.selecting;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== field || $[48] !== regions || $[49] !== state$.pickUpLocation) {
    t20 = () => {
      if (field === "dropOff") {
        const pickUpCountry = state$.pickUpLocation.get()?.countryCode;
        if (pickUpCountry) {
          return regions.filter(region => region.countryCode === pickUpCountry);
        }
      }
      return regions;
    };
    $[47] = field;
    $[48] = regions;
    $[49] = state$.pickUpLocation;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== field || $[52] !== t18 || $[53] !== t19 || $[54] !== t20) {
    t21 = <div className="h-full overflow-auto scrollbar-primary scrollbar-thin px-1"><ReactiveLocationSelectList name={field} $value={t18} onChange={t19} $regions={t20} /></div>;
    $[51] = field;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== floatingStyles || $[57] !== setFloating || $[58] !== t17 || $[59] !== t21) {
    t22 = <div ref={setFloating} className="transition-[height,opacity] h-0 border border-t-0 rounded-b-lg opacity-0 bg-white block peer-has-checked:opacity-100 peer-has-checked:h-auto peer-has-checked:bottom-6 overflow-hidden w-full inset-x-6 z-10" id={t17} style={floatingStyles}>{t21}</div>;
    $[56] = floatingStyles;
    $[57] = setFloating;
    $[58] = t17;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== t16 || $[62] !== t22) {
    t23 = <Show if={t16}>{t22}</Show>;
    $[61] = t16;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t15 || $[65] !== t23 || $[66] !== t6) {
    t24 = <div className="flex flex-col group">{t6}{t15}{t23}</div>;
    $[64] = t15;
    $[65] = t23;
    $[66] = t6;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  return t24;
}